import { useState } from "react";


const STRANDS = {
  name: "Strands Agents SDK",
  tagline: "The Developer SDK — Build individual agents fast",
  color: "#f59e0b",
  icon: "\u{1F9EC}",
  what: "Strands Agents is an open-source Python SDK from AWS for building AI agents with a simple, code-first approach. Think of it as the \"Flask/FastAPI of AI agents\" — minimal boilerplate, maximum flexibility. You define an agent with just a model, tools, and a prompt — and Strands handles the agentic loop (reason, act, observe, repeat).",
  caps: [
    ["Model-Driven Architecture", "The LLM IS the orchestrator. No complex state machines — the model decides which tools to call and when."],
    ["Multi-Model Support", "Works with Amazon Bedrock, OpenAI, Anthropic, LiteLLM, Ollama, and custom providers."],
    ["Built-in Tools", "20+ tools: file read/write, shell commands, API calls, RAG retrieval, code interpreter, etc."],
    ["Custom Tool Creation", "Decorate any Python function with @tool to make it agent-callable. Auto schema generation."],
    ["Conversation Memory", "Session persistence with DynamoDB, local file, or custom backends."],
    ["Streaming Responses", "Real-time token streaming for responsive UX."],
  ],
  code: `from strands import Agent
from strands.tools import calculator, web_search

# That's it — a working agent in 5 lines
agent = Agent(
    model="us.anthropic.claude-sonnet-4-20250514",
    tools=[calculator, web_search],
    system_prompt="You are a helpful research assistant."
)
response = agent("What's the GDP of Germany?")

# --- Custom Tool ---
from strands.tools import tool

@tool
def check_k8s_pods(namespace: str) -> str:
    """Check pod status in a K8s namespace"""
    import subprocess
    result = subprocess.run(
        ["kubectl", "get", "pods", "-n", namespace],
        capture_output=True, text=True
    )
    return result.stdout

k8s_agent = Agent(tools=[check_k8s_pods])`,
  uses: [
    "DevOps/SRE assistant that checks cluster health and reads logs",
    "Customer support bot with DB lookups and ticket creation",
    "Code review agent that reads PRs and runs linters",
    "HealthTech data pipeline agent that validates FHIR resources",
  ],
  best: "Building a SINGLE capable agent quickly with Python.",
};

const CORE = {
  name: "Amazon Bedrock Agent Core",
  tagline: "The Infrastructure Layer — Managed runtime services",
  color: "#8b5cf6",
  icon: "\u2699\uFE0F",
  what: "Agent Core is the foundational infrastructure layer (managed APIs/services) that provides runtime capabilities agents need — regardless of which SDK built them. Think of it as \"managed microservices for agents.\" It handles secure code execution, persistent memory, tool orchestration, identity/auth, and guardrails.",
  caps: [
    ["Code Interpreter (Sandboxed)", "Secure, isolated sandbox to execute Python code generated by agents."],
    ["Memory & Session Management", "Long-term and short-term memory APIs. Context across sessions without managing DynamoDB."],
    ["Tool Runtime", "Managed tool invocation with retries, timeouts, error handling. Native MCP support."],
    ["Guardrails Integration", "Content filtering, PII redaction, topic blocking — critical for HIPAA."],
    ["Identity & Access", "IAM-based auth for agent-to-service communication with scoped permissions."],
    ["Observability", "Built-in tracing and logging via CloudWatch."],
  ],
  code: `# Agent Core is INFRASTRUCTURE you configure.
# Here's how Strands uses Agent Core services:

from strands import Agent
from strands.tools.mcp import MCPClient

mcp = MCPClient(
    server_url="https://mcp.your-service.com/sse"
)

agent = Agent(
    model="us.anthropic.claude-sonnet-4-20250514",
    tools=[*mcp.get_tools()],
    # Agent Core handles sandboxed execution,
    # memory, guardrails behind the scenes
)

# --- Using Bedrock Agent Runtime ---
import boto3
bedrock = boto3.client("bedrock-agent-runtime")
response = bedrock.invoke_agent(
    agentId="YOUR_AGENT_ID",
    sessionId="user-session-123",
    inputText="Check my last lab results",
    enableTrace=True  # Observability built-in
)`,
  uses: [
    "Production agents with enterprise security (HIPAA, SOC2)",
    "Multi-tenant platforms with isolated agent execution",
    "HealthTech apps requiring PII redaction and audit trails",
    "Scaling agent workloads to thousands of concurrent sessions",
  ],
  best: "Production-grade agent INFRASTRUCTURE. Managed, secure, scalable runtime.",
};

const SQUAD = {
  name: "Agent Squad",
  tagline: "The Coordination Layer — Multiple agents working together",
  color: "#06b6d4",
  icon: "\uD83D\uDC65",
  what: "Agent Squad (formerly \"Multi-Agent Orchestrator\") is an open-source framework for systems where MULTIPLE specialized agents collaborate. It handles: \"I have 5 agents — how do I route requests to the right one, manage shared context, and coordinate work?\" Think of it as the Kubernetes of AI agents.",
  caps: [
    ["Intelligent Routing", "A classifier analyzes each message and routes it to the best specialist agent automatically."],
    ["Agent Registry", "Register/deregister agents dynamically with name, description, and capabilities metadata."],
    ["Shared Context", "Cross-agent memory so Agent B knows what Agent A already discussed with the user."],
    ["Multiple Agent Types", "Mix Bedrock agents, Strands agents, Lambda agents, or custom agents in one system."],
    ["Parallel Execution", "Route complex requests to multiple agents simultaneously and aggregate results."],
    ["Overlap Resolution", "When multiple agents could handle a request, picks the best fit based on context."],
  ],
  code: `from agent_squad import AgentSquad
from agent_squad import StrandsAgent, BedrockAgent

k8s_agent = StrandsAgent(
    name="K8sExpert",
    description="Handles Kubernetes: pods, deployments, scaling",
    tools=[kubectl_tool, helm_tool]
)

security_agent = StrandsAgent(
    name="SecurityExpert",
    description="Handles RBAC, network policies, CVE scanning",
    tools=[trivy_tool, opa_tool]
)

cost_agent = BedrockAgent(
    name="CostOptimizer",
    description="Cloud cost analysis and right-sizing",
    agent_id="BEDROCK_AGENT_ID"
)

squad = AgentSquad(
    agents=[k8s_agent, security_agent, cost_agent],
    classifier_model="us.anthropic.claude-sonnet-4-20250514"
)

# "My pods keep crashing" -> routes to K8sExpert
response = squad.route("My pods keep crashing")

# "Is my cluster HIPAA compliant?" -> SecurityExpert
response = squad.route("Is my EKS cluster HIPAA compliant?")`,
  uses: [
    "Enterprise help desks with IT, HR, Finance specialists",
    "Cloud ops combining K8s, networking, security, cost agents",
    "HealthTech patient portal: scheduling, labs, billing, triage",
    "DevOps ChatOps routing to CI/CD, monitoring, or infra agents",
  ],
  best: "Coordinating MULTIPLE agents as a team with intelligent routing.",
};

const ALL = { strands: STRANDS, agentcore: CORE, agentsquad: SQUAD };

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 16px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer",
      background: active ? "rgba(139,92,246,0.3)" : "rgba(255,255,255,0.05)",
      color: active ? "#e0e7ff" : "#94a3b8",
      border: active ? "1px solid #8b5cf6" : "1px solid rgba(255,255,255,0.1)",
    }}>{children}</button>
  );
}

function SubBtn({ active, color, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
      background: active ? color + "22" : "rgba(255,255,255,0.04)",
      color: active ? color : "#64748b",
      border: "1px solid " + (active ? color : "rgba(255,255,255,0.08)"),
    }}>{children}</button>
  );
}

function Architecture() {
  return (
    <div style={{ padding: 20, borderRadius: 16, background: "linear-gradient(135deg, #1e1b4b, #0f172a)" }}>
      <p style={{ textAlign: "center", color: "#fff", fontWeight: 700, fontSize: 18, marginBottom: 16 }}>How They Fit Together</p>
      
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <div style={{ border: "2px solid #06b6d4", borderRadius: 14, padding: 14, background: "rgba(8,145,178,0.08)", marginBottom: 12 }}>
          <p style={{ color: "#67e8f9", fontWeight: 700, textAlign: "center", fontSize: 15 }}>{"\uD83D\uDC65"} Agent Squad (Orchestration Layer)</p>
          <p style={{ color: "#a5f3fc", fontSize: 11, textAlign: "center", marginTop: 4 }}>Routes requests | Manages multiple agents | Aggregates responses</p>
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {["K8s Agent", "Security Agent", "Cost Agent", "Triage Agent"].map(a => (
              <div key={a} style={{ border: "1px solid #f59e0b", borderRadius: 8, padding: "8px 12px", background: "rgba(245,158,11,0.06)" }}>
                <p style={{ color: "#fbbf24", fontSize: 11, fontWeight: 700, textAlign: "center" }}>{"\u{1F9EC}"} {a}</p>
                <p style={{ color: "#fde68a", textAlign: "center", fontSize: 9 }}>Built with Strands</p>
              </div>
            ))}
          </div>
        </div>

        <p style={{ textAlign: "center", color: "#a78bfa", fontSize: 20, margin: "4px 0" }}>{"\u2B07\uFE0F"}</p>

        <div style={{ border: "2px solid #8b5cf6", borderRadius: 14, padding: 14, background: "rgba(139,92,246,0.06)", marginBottom: 16 }}>
          <p style={{ color: "#c4b5fd", fontWeight: 700, textAlign: "center", fontSize: 15 }}>{"\u2699\uFE0F"} Agent Core (Infrastructure Layer)</p>
          <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {["Code Sandbox", "Memory APIs", "Guardrails", "Tool Runtime", "Auth/IAM", "Observability"].map(s => (
              <span key={s} style={{ color: "#ddd6fe", background: "rgba(139,92,246,0.2)", borderRadius: 20, padding: "4px 10px", fontSize: 10 }}>{s}</span>
            ))}
          </div>
        </div>

        <div style={{ border: "1px dashed #64748b", borderRadius: 10, padding: 12 }}>
          <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
            <strong style={{ color: "#fff" }}>TL;DR:</strong> Strands = <span style={{ color: "#f59e0b" }}>build</span> one agent {"\u00B7"} Agent Core = <span style={{ color: "#8b5cf6" }}>run</span> it in production {"\u00B7"} Agent Squad = <span style={{ color: "#06b6d4" }}>coordinate</span> many agents
          </p>
        </div>
      </div>
    </div>
  );
}

function DeepDive({ activeDetail, setActiveDetail }) {
  const d = ALL[activeDetail];
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "center", flexWrap: "wrap" }}>
        <SubBtn active={activeDetail === "strands"} color="#f59e0b" onClick={() => setActiveDetail("strands")}>{"\u{1F9EC}"} Strands</SubBtn>
        <SubBtn active={activeDetail === "agentcore"} color="#8b5cf6" onClick={() => setActiveDetail("agentcore")}>{"\u2699\uFE0F"} Agent Core</SubBtn>
        <SubBtn active={activeDetail === "agentsquad"} color="#06b6d4" onClick={() => setActiveDetail("agentsquad")}>{"\uD83D\uDC65"} Squad</SubBtn>
      </div>

      <div style={{ borderRadius: 14, padding: 20, background: "rgba(255,255,255,0.03)", border: "1px solid " + d.color + "33" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>{d.icon}</span>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: d.color, margin: 0 }}>{d.name}</h2>
        </div>
        <p style={{ color: "#94a3b8", fontSize: 13, fontStyle: "italic", marginBottom: 20 }}>{d.tagline}</p>

        <h3 style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}>What is it?</h3>
        <p style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>{d.what}</p>

        <h3 style={{ color: "#fff", fontWeight: 600, marginBottom: 10 }}>Key Capabilities</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {d.caps.map((c, i) => (
            <div key={i} style={{ padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.03)" }}>
              <p style={{ fontWeight: 600, fontSize: 13, color: d.color, margin: 0 }}>{c[0]}</p>
              <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 4, margin: "4px 0 0" }}>{c[1]}</p>
            </div>
          ))}
        </div>

        <h3 style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}>Code Example</h3>
        <pre style={{ padding: 16, borderRadius: 10, fontSize: 11, overflowX: "auto", lineHeight: 1.6, background: "#0d1117", color: "#c9d1d9", border: "1px solid rgba(255,255,255,0.08)" }}>{d.code}</pre>

        <h3 style={{ color: "#fff", fontWeight: 600, marginTop: 20, marginBottom: 10 }}>Use Cases</h3>
        {d.uses.map((u, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 8, marginBottom: 4 }}>
            <span style={{ color: d.color }}>{"▸"}</span>
            <p style={{ color: "#cbd5e1", fontSize: 13, margin: 0 }}>{u}</p>
          </div>
        ))}

        <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: d.color + "11", border: "1px solid " + d.color + "33" }}>
          <p style={{ fontSize: 13, margin: 0 }}>
            <strong style={{ color: d.color }}>Best for: </strong>
            <span style={{ color: "#cbd5e1" }}>{d.best}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function Compare() {
  const rows = [
    ["What is it?", "Python SDK (open-source)", "Managed AWS services", "Orchestration framework (open-source)"],
    ["Analogy", "Flask for agents", "AWS Lambda for agents", "Kubernetes for agents"],
    ["Scope", "Single agent", "Agent infrastructure", "Multi-agent systems"],
    ["Open Source?", "Yes (GitHub)", "No (managed service)", "Yes (GitHub)"],
    ["When to use", "Prototyping, single-agent", "Production, compliance", "Complex multi-domain"],
    ["Learning Curve", "Low — 5 lines", "Medium — AWS infra", "Medium — design patterns"],
    ["Model Lock-in?", "No — multi-provider", "Bedrock-centric", "No — any agent type"],
    ["HealthTech Fit", "Quick prototypes", "HIPAA guardrails", "Specialist agent portals"],
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ padding: 10, textAlign: "left", color: "#94a3b8", borderBottom: "1px solid #334155" }}></th>
            <th style={{ padding: 10, textAlign: "center", color: "#f59e0b", borderBottom: "1px solid #334155" }}>{"\u{1F9EC}"} Strands</th>
            <th style={{ padding: 10, textAlign: "center", color: "#8b5cf6", borderBottom: "1px solid #334155" }}>{"\u2699\uFE0F"} Core</th>
            <th style={{ padding: 10, textAlign: "center", color: "#06b6d4", borderBottom: "1px solid #334155" }}>{"\uD83D\uDC65"} Squad</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              <td style={{ padding: 10, fontWeight: 600, color: "#cbd5e1", borderBottom: "1px solid #1e293b" }}>{r[0]}</td>
              <td style={{ padding: 10, textAlign: "center", color: "#e2e8f0", borderBottom: "1px solid #1e293b" }}>{r[1]}</td>
              <td style={{ padding: 10, textAlign: "center", color: "#e2e8f0", borderBottom: "1px solid #1e293b" }}>{r[2]}</td>
              <td style={{ padding: 10, textAlign: "center", color: "#e2e8f0", borderBottom: "1px solid #1e293b" }}>{r[3]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Decide() {
  const [a, setA] = useState({});

  const qs = [
    { id: "count", q: "How many agents do you need?", opts: ["Just one", "Multiple specialists"] },
    { id: "stage", q: "What stage are you at?", opts: ["Prototyping", "Production"] },
    { id: "compliance", q: "Need HIPAA / compliance?", opts: ["Yes", "Not yet"] },
  ];

  const getRec = () => {
    if (!a.count || !a.stage || !a.compliance) return null;
    const one = a.count === "Just one";
    const proto = a.stage === "Prototyping";
    const hipaa = a.compliance === "Yes";

    if (one && proto && !hipaa) return ["Start with Strands SDK", "Build a single agent fast. Add Agent Core later for production.", "#f59e0b"];
    if (one && proto && hipaa) return ["Strands + Agent Core", "Use Strands to build, Agent Core for HIPAA guardrails.", "#a78bfa"];
    if (one && !proto) return ["Strands + Agent Core", "Build with Strands, run on Agent Core for production security and observability.", "#a78bfa"];
    if (!one && proto) return ["Agent Squad + Strands", "Build each specialist with Strands, coordinate them with Agent Squad.", "#06b6d4"];
    return ["All Three!", "Strands to build agents, Squad to coordinate, Agent Core for production infra.", "#10b981"];
  };

  const rec = getRec();

  return (
    <div style={{ padding: 20, borderRadius: 16, background: "linear-gradient(135deg, #1a1a2e, #16213e)" }}>
      <p style={{ color: "#fff", fontWeight: 700, fontSize: 18, textAlign: "center", marginBottom: 16 }}>{"\uD83E\uDDED"} Which Should You Use?</p>

      {qs.map(q => (
        <div key={q.id} style={{ marginBottom: 16 }}>
          <p style={{ color: "#cbd5e1", fontSize: 14, marginBottom: 8 }}>{q.q}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {q.opts.map(o => (
              <button key={o} onClick={() => setA(p => ({ ...p, [q.id]: o }))}
                style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
                  background: a[q.id] === o ? "rgba(99,102,241,0.4)" : "rgba(255,255,255,0.05)",
                  color: a[q.id] === o ? "#e0e7ff" : "#94a3b8",
                  border: a[q.id] === o ? "1px solid #818cf8" : "1px solid rgba(255,255,255,0.1)",
                }}>{o}</button>
            ))}
          </div>
        </div>
      ))}

      {rec && (
        <div style={{ marginTop: 8, padding: 16, borderRadius: 12, border: "1px solid " + rec[2], background: "rgba(0,0,0,0.3)" }}>
          <p style={{ fontWeight: 700, fontSize: 17, color: rec[2], margin: 0 }}>{"\u2192"} {rec[0]}</p>
          <p style={{ color: "#cbd5e1", fontSize: 13, marginTop: 6, margin: "6px 0 0" }}>{rec[1]}</p>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState("strands");

  return (
    <div style={{ minHeight: "100vh", color: "#fff", background: "linear-gradient(180deg, #0f0a1a 0%, #1a1033 50%, #0d1117 100%)", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "16px 16px 48px" }}>
        <div style={{ textAlign: "center", marginBottom: 24, paddingTop: 16 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, background: "linear-gradient(90deg, #f59e0b, #8b5cf6, #06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            AWS Agent Stack
          </h1>
          <p style={{ color: "#94a3b8", marginTop: 4 }}>Strands {"\u00B7"} Agent Core {"\u00B7"} Agent Squad</p>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 24, justifyContent: "center", flexWrap: "wrap" }}>
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")}>{"\uD83C\uDFD7\uFE0F"} Architecture</TabBtn>
          <TabBtn active={tab === "deep"} onClick={() => setTab("deep")}>{"\uD83D\uDD0D"} Deep Dive</TabBtn>
          <TabBtn active={tab === "compare"} onClick={() => setTab("compare")}>{"\u2696\uFE0F"} Compare</TabBtn>
          <TabBtn active={tab === "decide"} onClick={() => setTab("decide")}>{"\uD83E\uDDED"} Which One?</TabBtn>
        </div>

        {tab === "overview" && <Architecture />}
        {tab === "deep" && <DeepDive activeDetail={detail} setActiveDetail={setDetail} />}
        {tab === "compare" && <Compare />}
        {tab === "decide" && <Decide />}
      </div>
    </div>
  );
}